import React, {
  useState,
  useEffect,
  createContext,
  useRef,
  useCallback,
} from "react";
import { webAPIs } from "./config/webAPIs";
import { notification } from "antd";
import axiosInstance from "./config/axiosInstance";

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState([
    {
      role: "assistant",
      content: "Hello. How can I help you?",
    },
  ]);
  const [files, setFiles] = useState([]);
  const [checked, setChecked] = useState(true);
  const [adminPrompt, setAdminPrompt] = useState("");
  const [kSystemPrompt, setKSystemPrompt] = useState("");
  const [kUserPrompt, setKUserPrompt] = useState("");
  const [kFontSize, setKFontSize] = useState(0);
  const [inputValue, setInputValue] = useState(0);
  const [systemRoles, setSystemRoles] = useState([]);
  const timeoutIdRef = useRef(null);
  const [selectedDocIds, setSelectedDocIds] = useState(() => {
    const savedIds = localStorage.getItem("selectedDocIds");
    return savedIds ? JSON.parse(savedIds) : [];
  });
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    const raw = localStorage.getItem("selectedProjectId");
    return raw !== null ? Number(raw) : undefined;
  });

  useEffect(() => {
    localStorage.setItem("selectedDocIds", JSON.stringify(selectedDocIds));
  }, [selectedDocIds]);

  const resetTimeout = () => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
    const id = setTimeout(() => {
      console.log("Session expired due to inactivity. Please log in again.");
      logout();
    }, 7200000);
    timeoutIdRef.current = id;
  };

  useEffect(() => {
    const handleActivity = () => {
      resetTimeout();
    };

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keypress", handleActivity);
    resetTimeout();

    return () => {
      clearTimeout(timeoutIdRef.current);
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keypress", handleActivity);
    };
  }, []);

  useEffect(() => {
    refreshUserData();
  }, []);

  const refreshUserData = async () => {
    try {
      const { data } = await axiosInstance.get(webAPIs.getUser);
      if (data.success && data.user) {
        setUser(data.user);
        setLoggedIn(true);
      } else {
        setLoggedIn(false);
        setUser(null);
      }
    } catch (err) {
      // If unauthorized or any error, clear user state
      console.error("Failed to refresh user", err);
      setLoggedIn(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (loggedIn) {
      handleResetMessage();
    }
  }, [loggedIn]);

  useEffect(() => {
    if (!loggedIn) {
      return undefined;
    }
    const typingTimer = setTimeout(() => {
      handleChangeAdminPrompt();
    }, 500);
    return () => clearTimeout(typingTimer);
  }, [loggedIn, adminPrompt, inputValue, checked, kSystemPrompt, kUserPrompt, kFontSize]);

  const handleChangeAdminPrompt = async () => {
    if (adminPrompt) {
      if (user) {
        await axiosInstance.post(webAPIs.changeSystemPrompt, {
          systemPrompt: adminPrompt,
          temp: inputValue,
          kSystemPrompt,
          kUserPrompt,
          kFontSize,
          checked,
        });
      }
    }
  };

  const handleResetMessage = async () => {
    if (user && selectedProjectId) {
      try {
        const response = await axiosInstance.get(
          `${webAPIs.getConversation}?projectId=${selectedProjectId}`
        );
        if (response.data.success) {
          setMessage(response.data.result.conversation);
          setAdminPrompt(response.data.result.systemPrompt);
          setInputValue(response.data.result.temp);
          setFiles(response.data.result.files);
          setChecked(response.data.result.checked);
          setKSystemPrompt(response.data.result.kSystemPrompt);
          setKUserPrompt(response.data.result.kUserPrompt);
          setKFontSize(response.data.result.kFontSize);
        }
        getSystemRoles();
      } catch (error) {
        console.log(error);
      }
    }
  };

  const getSystemRoles = useCallback(async () => {
    try {
      const response = await axiosInstance.get(webAPIs.getSystemRoles);
      if (response.data.success) {
        setSystemRoles(response.data.data);
      }
    } catch (e) {
      notification.error({
        description: e.response?.data?.error || "Server Error",
        duration: 2,
      });
    }
  }, []);

  const login = (userData) => {
    setUser(userData);
    setLoggedIn(true);
    resetTimeout();
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await axiosInstance.post(webAPIs.logout);
    } catch (error) {
      console.error("Logout request failed", error);
    } finally {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      localStorage.removeItem("selectedDocIds");
      localStorage.removeItem("selectedProjectId");
      localStorage.removeItem("rememberedEmail");

      // Manually clear cookies as a backup
      document.cookie = "access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      document.cookie = "refresh_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      document.cookie = "csrf_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

      setLoggedIn(false);
      setUser(null);
      setSelectedDocIds([]);
      setSelectedProjectId(undefined);
      setMessage([
        {
          role: "assistant",
          content: "Hello. How can I help you?",
        },
      ]);
      setFiles([]);
      setProjects([]);
      // Force a hard reload to clear all state and cookies
      window.location.href = "/";
    }
  };

  // Fetch projects for the user's account
  const fetchProjects = async (accountId) => {
    if (!accountId) return;
    try {
      const res = await axiosInstance.get(webAPIs.fetchProjects(accountId));
      const data = res.data || [];
      setProjects(res.data);
      // If no project is selected, select the first one
      if (
        (!selectedProjectId ||
          !data.find((p) => p.id === Number(selectedProjectId))) &&
        data.length > 0
      ) {
        setSelectedProjectId(data[0].id);
        localStorage.setItem("selectedProjectId", data[0].id);
      }
    } catch (e) {
      setProjects([]);
    }
  };

  // When user is set, fetch projects
  useEffect(() => {
    console.log("User changed:", user);
    if (user && user.accountId) {
      fetchProjects(user.accountId);
    }
  }, [user]);

  // When selectedProjectId changes, fetch conversation/files
  useEffect(() => {
    if (loggedIn && selectedProjectId) {
      handleResetMessage();
    }
  }, [selectedProjectId, loggedIn]);

  // Update selectedProjectId in localStorage and context
  const handleSelectProject = (projectId) => {
    const id = Number(projectId);
    setSelectedProjectId(id);
    localStorage.setItem("selectedProjectId", id);
  };

  return (
    <div className="bg-botColor h-full">
      <AuthContext.Provider
        value={{
          loggedIn,
          user,
          isLoading,
          login,
          logout,
          updateUser,
          message,
          setMessage,
          files,
          setFiles,
          checked,
          setChecked,
          adminPrompt,
          setAdminPrompt,
          kSystemPrompt,
          setKSystemPrompt,
          kUserPrompt,
          setKUserPrompt,
          kFontSize,
          setKFontSize,
          inputValue,
          setInputValue,
          systemRoles,
          setSystemRoles,
          handleResetMessage,
          setLoading,
          loading,
          refreshUserData,
          selectedDocIds,
          setSelectedDocIds,
          projects,
          selectedProjectId,
          setSelectedProjectId: handleSelectProject,
          fetchProjects,
        }}
      >
        {children}
      </AuthContext.Provider>
    </div>
  );
};

export { AuthContext, AuthProvider };
